import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, Trash2, Save, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/apiError';
import { RateCardLibrary } from '../components/RateCardLibrary';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Default surcharges
const DEFAULT_SURCHARGES = [
  { description: "ERP & Parking Charges", rate: "At Cost" },
  { description: "Any Other Charges", rate: "As Per Receipt" },
  { description: "Night 1900hrs - 0000hrs", rate: "Rate x1.5 Per Hr" },
  { description: "Midnight 0000 Hrs - 0800 Hrs", rate: "Rate x1.5 Per Hr" },
  { description: "Sunday & Public Holidays 24 Hrs", rate: "Rate x2 Per Hr" },
];

export const EditQuotation = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [pricing, setPricing] = useState(null);
  const [formData, setFormData] = useState({
    company_name: '',
    company_contact: '',
    company_email: '',
    company_address: '',
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    site_contact_name: '',
    site_contact_phone: '',
    validity_days: 30,
    notes: ''
  });
  const [lineItems, setLineItems] = useState([
    { description: '', quantity: 1, hours: '', unit_price: 0, amount: 0 }
  ]);
  const [surcharges, setSurcharges] = useState([...DEFAULT_SURCHARGES]);
  const [documentNumber, setDocumentNumber] = useState('');
  const [origDocNumber, setOrigDocNumber] = useState('');

  useEffect(() => {
    fetchQuotation();
    fetchPricing();
  }, [id]);

  const fetchQuotation = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/quotations-billing/${id}`);
      const data = response.data;
      setDocumentNumber(data.document_number || '');
      setOrigDocNumber(data.document_number || '');
      
      setFormData({
        company_name: data.company_name || '',
        company_contact: data.company_contact || '',
        company_email: data.company_email || '',
        company_address: data.company_address || '',
        customer_name: data.customer_name || '',
        customer_phone: data.customer_phone || '',
        customer_email: data.customer_email || '',
        site_contact_name: data.site_contact_name || '',
        site_contact_phone: data.site_contact_phone || '',
        validity_days: data.validity_days || 30,
        notes: data.notes || ''
      });
      
      if (data.line_items && data.line_items.length > 0) {
        setLineItems(data.line_items.map(item => ({
          description: item.description || '',
          quantity: item.quantity || 1,
          hours: item.hours != null ? item.hours : '',
          unit_price: item.unit_price || 0,
          amount: item.amount || 0
        })));
      }
      
      // Load surcharges or use defaults
      if (data.surcharges && data.surcharges.length > 0) {
        setSurcharges(data.surcharges);
      }
    } catch (error) {
      toast.error('Failed to load quotation');
      console.error(error);
    } finally {
      setFetchLoading(false);
    }
  };

  const fetchPricing = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/pricing`);
      setPricing(response.data);
    } catch (error) {
      console.error('Failed to load pricing', error);
    }
  };
  
  // Surcharge functions
  const addSurcharge = () => {
    setSurcharges([...surcharges, { description: '', rate: '' }]);
  };

  const removeSurcharge = (index) => {
    setSurcharges(surcharges.filter((_, i) => i !== index));
  };

  const updateSurcharge = (index, field, value) => {
    const updated = [...surcharges];
    updated[index][field] = value;
    setSurcharges(updated);
  };

  const addLineItem = () => {
    setLineItems([...lineItems, { description: '', quantity: 1, hours: '', unit_price: 0, amount: 0 }]);
  };

  const addRateFromCard = (item) => {
    const newItem = { ...item, hours: '' };
    setLineItems((prev) => {
      if (prev.length === 1 && !prev[0].description && !(prev[0].amount > 0)) return [newItem];
      return [...prev, newItem];
    });
  };

  const removeLineItem = (index) => {
    const updated = lineItems.filter((_, i) => i !== index);
    setLineItems(updated);
  };

  const updateLineItem = (index, field, value) => {
    const updated = [...lineItems];
    updated[index][field] = value;
    
    if (field === 'quantity' || field === 'hours' || field === 'unit_price') {
      const qty = parseFloat(updated[index].quantity || 0);
      const hours = parseFloat(updated[index].hours || 0);
      const price = parseFloat(updated[index].unit_price || 0);
      updated[index].amount = hours > 0 ? qty * hours * price : qty * price;
    }
    
    setLineItems(updated);
  };

  const selectService = (index, serviceId, rateType) => {
    if (!pricing) return;
    
    let service = null;
    const allServices = [
      ...pricing.services.lorry_cranes,
      ...pricing.services.trailers,
      ...pricing.services.tailgate
    ];
    
    service = allServices.find(s => s.id === serviceId);
    if (serviceId === 'manpower') {
      service = pricing.services.manpower;
    }
    
    if (!service) return;
    
    const updated = [...lineItems];
    let unitPrice = 0;
    let description = service.name;
    
    if (rateType === 'hourly' && service.hourly_rate) {
      unitPrice = service.hourly_rate;
      description += ' - Hourly Rate';
    } else if (rateType === 'half_day' && service.half_day_rate) {
      unitPrice = service.half_day_rate;
      description += ' - Half Day';
    } else if (rateType === 'per_trip' && service.per_trip_rate) {
      unitPrice = service.per_trip_rate;
      description += ' - Per Trip';
    } else if (rateType === 'per_day' && service.per_day_rate) {
      unitPrice = service.per_day_rate;
      description += ' - Per Day';
    }
    
    updated[index].description = description;
    updated[index].unit_price = unitPrice;
    const qty = parseFloat(updated[index].quantity || 0);
    const hours = parseFloat(updated[index].hours || 0);
    updated[index].amount = hours > 0 ? qty * hours * unitPrice : qty * unitPrice;
    
    setLineItems(updated);
  };

  const calculateSubtotal = () => {
    return lineItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const quotationData = {
        ...formData,
        line_items: lineItems.map(item => ({
          description: item.description,
          quantity: parseFloat(item.quantity),
          hours: item.hours !== '' && item.hours != null ? parseFloat(item.hours) : null,
          unit_price: parseFloat(item.unit_price),
          amount: parseFloat(item.amount)
        })),
        surcharges: surcharges.filter(s => s.description && s.rate)
      };

      const response = await axios.put(`${BACKEND_URL}/api/quotations-billing/${id}`, quotationData);

      if (documentNumber && documentNumber !== origDocNumber) {
        await axios.patch(`${BACKEND_URL}/api/quotations-billing/${id}/document-number`, { document_number: documentNumber });
      }

      if (response.data.success) {
        toast.success('Quotation updated successfully');
        navigate(`/admin/quotations/${id}`);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update quotation'));
    } finally {
      setLoading(false);
    }
  };

  const subtotal = calculateSubtotal();

  if (fetchLoading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-40 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-40">
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate(`/admin/quotations/${id}`)}>
              <ArrowLeft size={20} />
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Edit Quotation</h1>
              <p className="text-gray-600 mt-1">Update quotation details</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <form onSubmit={handleSubmit}>
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left Column - Company & Customer Details */}
            <div className="lg:col-span-2 space-y-6">
              {/* Document Number */}
              <Card>
                <CardHeader>
                  <CardTitle>Document Number</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="max-w-xs space-y-2">
                    <Label>Quotation Number</Label>
                    <Input
                      data-testid="edit-qt-number-input"
                      value={documentNumber}
                      onChange={(e) => setDocumentNumber(e.target.value)}
                    />
                    <p className="text-xs text-gray-500">Change carefully — duplicates are rejected automatically.</p>
                  </div>
                </CardContent>
              </Card>
              {/* Company Details */}
              <Card>
                <CardHeader>
                  <CardTitle>Company Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Company Name *</Label>
                      <Input
                        value={formData.company_name}
                        onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                        placeholder="ABC Construction Pte Ltd"
                        required
                      />
                    </div>
                    <div>
                      <Label>Company Contact *</Label>
                      <Input
                        type="tel"
                        value={formData.company_contact}
                        onChange={(e) => setFormData({...formData, company_contact: e.target.value})}
                        placeholder="6512345678"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Company Email *</Label>
                    <Input
                      type="email"
                      value={formData.company_email}
                      onChange={(e) => setFormData({...formData, company_email: e.target.value})}
                      placeholder="info@company.com"
                      required
                    />
                  </div>
                  <div>
                    <Label>Company Address</Label>
                    <Textarea
                      value={formData.company_address}
                      onChange={(e) => setFormData({...formData, company_address: e.target.value})}
                      placeholder="123 Main Street, Singapore 123456"
                      rows={2}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Customer Details (Contact Person) */}
              <Card>
                <CardHeader>
                  <CardTitle>Customer Details (Contact Person)</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Contact Name *</Label>
                      <Input
                        value={formData.customer_name}
                        onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                        placeholder="John Doe"
                        required
                      />
                    </div>
                    <div>
                      <Label>Contact Phone *</Label>
                      <Input
                        type="tel"
                        value={formData.customer_phone}
                        onChange={(e) => setFormData({...formData, customer_phone: e.target.value})}
                        placeholder="91234567"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Contact Email *</Label>
                    <Input
                      type="email"
                      value={formData.customer_email}
                      onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
                      placeholder="john@company.com"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Site Contact Name</Label>
                      <Input
                        value={formData.site_contact_name}
                        onChange={(e) => setFormData({...formData, site_contact_name: e.target.value})}
                        placeholder="e.g., Mr Tan"
                        data-testid="edit-qt-site-contact-name"
                      />
                    </div>
                    <div>
                      <Label>Site Contact Phone</Label>
                      <Input
                        value={formData.site_contact_phone}
                        onChange={(e) => setFormData({...formData, site_contact_phone: e.target.value})}
                        placeholder="e.g., 9123 4567"
                        data-testid="edit-qt-site-contact-phone"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle>Line Items</CardTitle>
                    <Button type="button" onClick={addLineItem} variant="outline">
                      <Plus size={16} className="mr-2" />
                      Add Item
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <RateCardLibrary onAdd={addRateFromCard} />
                  <div className="space-y-4">
                    {lineItems.map((item, index) => (
                      <div key={index} className="border p-4 rounded-lg space-y-4">
                        <div className="flex justify-between items-start">
                          <p className="font-semibold">Item {index + 1}</p>
                          {lineItems.length > 1 && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => removeLineItem(index)}
                            >
                              <Trash2 size={16} />
                            </Button>
                          )}
                        </div>

                        {pricing && (
                          <div>
                            <Label>Quick Select Service</Label>
                            <div className="mt-2">
                              <select 
                                className="w-full h-10 px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                onChange={(e) => {
                                  if (e.target.value) {
                                    const [serviceId, rateType] = e.target.value.split('|');
                                    selectService(index, serviceId, rateType);
                                    e.target.value = '';
                                  }
                                }}
                                defaultValue=""
                              >
                                <option value="" disabled>Select service...</option>
                                <optgroup label="Lorry Cranes">
                                  {pricing.services.lorry_cranes.map(crane => (
                                    <React.Fragment key={crane.id}>
                                      {crane.hourly_rate && (
                                        <option value={`${crane.id}|hourly`}>
                                          {crane.name} - Hourly
                                        </option>
                                      )}
                                      {crane.per_trip_rate && (
                                        <option value={`${crane.id}|per_trip`}>
                                          {crane.name} - Per Trip
                                        </option>
                                      )}
                                      {crane.half_day_rate && (
                                        <option value={`${crane.id}|half_day`}>
                                          {crane.name} - Half Day
                                        </option>
                                      )}
                                      {crane.per_day_rate && (
                                        <option value={`${crane.id}|per_day`}>
                                          {crane.name} - Per Day
                                        </option>
                                      )}
                                    </React.Fragment>
                                  ))}
                                </optgroup>
                                <optgroup label="Trailers">
                                  {pricing.services.trailers.map(trailer => (
                                    <React.Fragment key={trailer.id}>
                                      {trailer.hourly_rate && (
                                        <option value={`${trailer.id}|hourly`}>
                                          {trailer.name} - Hourly
                                        </option>
                                      )}
                                      {trailer.half_day_rate && (
                                        <option value={`${trailer.id}|half_day`}>
                                          {trailer.name} - Half Day
                                        </option>
                                      )}
                                      {trailer.per_day_rate && (
                                        <option value={`${trailer.id}|per_day`}>
                                          {trailer.name} - Per Day
                                        </option>
                                      )}
                                    </React.Fragment>
                                  ))}
                                </optgroup>
                                <optgroup label="Tailgate">
                                  {pricing.services.tailgate.map(tg => (
                                    <React.Fragment key={tg.id}>
                                      {tg.hourly_rate && (
                                        <option value={`${tg.id}|hourly`}>
                                          {tg.name} - Hourly
                                        </option>
                                      )}
                                      {tg.half_day_rate && (
                                        <option value={`${tg.id}|half_day`}>
                                          {tg.name} - Half Day
                                        </option>
                                      )}
                                      {tg.per_day_rate && (
                                        <option value={`${tg.id}|per_day`}>
                                          {tg.name} - Per Day
                                        </option>
                                      )}
                                    </React.Fragment>
                                  ))}
                                </optgroup>
                                <optgroup label="Other">
                                  <option value="manpower|per_day">
                                    Manpower - Per Day
                                  </option>
                                </optgroup>
                              </select>
                            </div>
                          </div>
                        )}

                        <div>
                          <Label>Description *</Label>
                          <Input
                            value={item.description}
                            onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                            required
                            data-testid={`line-item-description-${index}`}
                          />
                        </div>

                        <div className="grid grid-cols-4 gap-4">
                          <div>
                            <Label>Quantity</Label>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={item.quantity}
                              onChange={(e) => updateLineItem(index, 'quantity', e.target.value)}
                            />
                          </div>
                          <div>
                            <Label>Hours</Label>
                            <Input
                              type="number"
                              step="0.5"
                              min="0"
                              value={item.hours || ''}
                              onChange={(e) => updateLineItem(index, 'hours', e.target.value)}
                              placeholder="e.g. 3"
                              data-testid="edit-qt-line-hours"
                            />
                          </div>
                          <div>
                            <Label>Unit Price ($)</Label>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={item.unit_price}
                              onChange={(e) => updateLineItem(index, 'unit_price', e.target.value)}
                            />
                          </div>
                          <div>
                            <Label>Amount ($)</Label>
                            <Input
                              type="number"
                              value={item.amount.toFixed(2)}
                              readOnly
                              className="bg-gray-50"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Surcharges Section */}
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle>Additional Surcharges</CardTitle>
                    <Button type="button" onClick={addSurcharge} variant="outline" size="sm">
                      <Plus size={16} className="mr-2" />
                      Add Surcharge
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {surcharges.map((surcharge, index) => (
                      <div key={index} className="flex gap-3 items-center">
                        <div className="w-8 text-center text-gray-500 font-medium">{index + 1}</div>
                        <div className="flex-1">
                          <Input
                            value={surcharge.description}
                            onChange={(e) => updateSurcharge(index, 'description', e.target.value)}
                            placeholder="Description"
                          />
                        </div>
                        <div className="w-40">
                          <Input
                            value={surcharge.rate}
                            onChange={(e) => updateSurcharge(index, 'rate', e.target.value)}
                            placeholder="Rate"
                          />
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeSurcharge(index)}
                          className="text-red-600"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Additional Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Validity (Days)</Label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.validity_days}
                      onChange={(e) => setFormData({...formData, validity_days: parseInt(e.target.value)})}
                    />
                  </div>
                  <div>
                    <Label>Notes</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      rows={4}
                      placeholder="Additional notes or terms..."
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Summary */}
            <div>
              <Card className="sticky top-24">
                <CardHeader>
                  <CardTitle>Quotation Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Subtotal:</span>
                      <span className="font-semibold">${subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">GST (0%):</span>
                      <span className="font-semibold">$0.00</span>
                    </div>
                    <div className="border-t pt-2 flex justify-between">
                      <span className="font-bold">Total:</span>
                      <span className="font-bold text-xl text-blue-600">${subtotal.toFixed(2)}</span>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <Button
                      type="submit"
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                      disabled={loading}
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Saving...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Save size={20} />
                          Save Changes
                        </span>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
